import React from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import { getFeedbackSites } from '@app/api/feedback-site';
import type { IFeedbackSite } from '@app/interfaces/feedback-site.interface';
import type { IClinic } from '@app/interfaces/clinic.interface';
import { updateClinicFbSites } from '@app/api/clinic';
import MoreIcon from '@mui/icons-material/MoreVert';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import useDropdownMenu from '@app/hooks/useDropdownMenu';
import { useEditDoctorListDialog } from '@pages/clinicPage/lib';

type Props = {
  clinic: IClinic;
  setClinic: (clinic: IClinic) => void;
};

function SettingBlockFeedbackSitesTab({ clinic, setClinic }: Props) {
  const [feedbackSites, setFeedbackSites] = React.useState<IFeedbackSite[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [editedFeedbackSite, setEditedFeedbackSite] = React.useState<IFeedbackSite>();
  const [editedUrl, setEditedUrl] = React.useState('');
  const [anchorsList, openMenu, closeMenu, closeAllMenu] = useDropdownMenu();

  const { Modal: EditDoctorListModal, modalProps, openModal } = useEditDoctorListDialog();

  React.useEffect(() => {
    getFeedbackSites()
      .then((feedbackSites) => setFeedbackSites(feedbackSites || []))
      .finally(() => setIsLoading(false));
  }, []);

  const onSwitchToggle = (feedbackSite: IFeedbackSite) => async () => {
    if (!feedbackSite.id) {
      return;
    }

    const fbSites = clinic.fbSites || [];
    const updatedFbSites = isChecked(feedbackSite.id)
      ? fbSites.map((fbSite) =>
          fbSite.id === feedbackSite.id ? { ...fbSite, enabled: false } : fbSite
        )
      : fbSites.some(({ id }) => id === feedbackSite.id)
        ? fbSites.map((fbSite) =>
            fbSite.id === feedbackSite.id ? { ...fbSite, enabled: true } : fbSite
          )
        : [
            ...fbSites,
            {
              id: feedbackSite.id,
              url: feedbackSite.url,
              enabled: true,
              doctorFeedbackEnabled: false,
            },
          ];
    const updatedClinic = await updateClinicFbSites(clinic.id, updatedFbSites);

    if (updatedClinic) {
      setClinic(updatedClinic);
    }
  };

  const isChecked = (feedbackSiteId: number) =>
    (clinic.fbSites || []).some(({ id, enabled }) => id === feedbackSiteId && enabled);

  const getClinicFbSiteUrl = (feedbackSite: IFeedbackSite) => {
    if (!feedbackSite.id) {
      return '';
    }

    return clinic.fbSites?.find(({ id }) => id === feedbackSite.id)?.url || '';
  };

  const openLinkEditor = (feedbackSite: IFeedbackSite) => {
    setEditedFeedbackSite(feedbackSite);
    setEditedUrl(getClinicFbSiteUrl(feedbackSite));
    closeAllMenu();
  };

  const closeLinkEditor = () => {
    setEditedFeedbackSite(undefined);
    setEditedUrl('');
  };

  const openDoctorList = (site: IFeedbackSite) => {
    openModal(site);
    closeAllMenu();
  };

  const saveLink = async () => {
    if (!editedFeedbackSite?.id) {
      return;
    }

    const fbSites = clinic.fbSites || [];
    const hasEditedFbSite = fbSites.some(({ id }) => id === editedFeedbackSite.id);
    const updatedClinic = await updateClinicFbSites(
      clinic.id,
      hasEditedFbSite
        ? fbSites.map((fbSite) =>
            fbSite.id === editedFeedbackSite.id
              ? {
                  ...fbSite,
                  url: editedUrl,
                  enabled: editedUrl ? fbSite.enabled : false,
                }
              : fbSite
          )
        : [
            ...fbSites,
            {
              id: editedFeedbackSite.id,
              url: editedUrl,
              enabled: false,
              doctorFeedbackEnabled: false,
            },
          ]
    );

    if (updatedClinic) {
      setClinic(updatedClinic);
      closeLinkEditor();
    }
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  return (
    <>
      <List dense={true} sx={{ width: 450 }}>
        {feedbackSites.map((feedbackSite, idx) => (
          <ListItem
            key={feedbackSite.id}
            secondaryAction={
              <Box>
                <IconButton
                  size='large'
                  aria-label='feedback site actions'
                  aria-controls='menu-appbar'
                  aria-haspopup='true'
                  onClick={openMenu(idx)}
                  color='inherit'
                >
                  <MoreIcon />
                </IconButton>

                <Menu
                  id='menu-appbar'
                  anchorEl={anchorsList?.[idx] || null}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorsList?.[idx])}
                  onClose={closeMenu(-1)}
                >
                  <MenuItem
                    component='a'
                    href={getClinicFbSiteUrl(feedbackSite)}
                    target='_blank'
                    rel='noreferrer'
                  >
                    <ListItemIcon>
                      <LinkIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText>Открыть ссылку</ListItemText>
                  </MenuItem>

                  <Divider />

                  <MenuItem
                    onClick={() => openLinkEditor(feedbackSite)}
                    disabled={!feedbackSite.id}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText>Редактировать ссылку</ListItemText>
                  </MenuItem>

                  <Divider />

                  <MenuItem
                    onClick={() => openDoctorList(feedbackSite)}
                    disabled={!feedbackSite.isDoctorFeedback}
                  >
                    <ListItemIcon>
                      <PersonIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText>Доктора</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            }
            sx={{ borderBottom: '1px solid lightgrey' }}
          >
            <Switch
              edge='start'
              onChange={onSwitchToggle(feedbackSite)}
              checked={feedbackSite.id ? isChecked(feedbackSite.id) : false}
              disabled={!feedbackSite.id || !getClinicFbSiteUrl(feedbackSite)}
              sx={{ marginRight: 2 }}
            />

            <ListItemAvatar>
              <Avatar>{feedbackSite.name.slice(0, 2).toUpperCase()}</Avatar>
            </ListItemAvatar>

            <ListItemText
              primary={feedbackSite.name}
              secondary={getClinicFbSiteUrl(feedbackSite) || '—'}
              secondaryTypographyProps={{
                sx: {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </ListItem>
        ))}
      </List>

      <Dialog open={Boolean(editedFeedbackSite)} onClose={closeLinkEditor} maxWidth='sm' fullWidth>
        <DialogTitle>{editedFeedbackSite?.name}</DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            id='fb-site-url'
            label='Ссылка'
            fullWidth
            variant='outlined'
            value={editedUrl}
            onChange={(event) => setEditedUrl(event.target.value)}
          />
        </DialogContent>

        <DialogActions sx={{ padding: '0 24px 24px' }}>
          <Button onClick={closeLinkEditor}>Отмена</Button>

          <Button onClick={saveLink} variant='contained'>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <EditDoctorListModal {...modalProps} clinic={clinic} setClinic={setClinic} />
    </>
  );
}

export default SettingBlockFeedbackSitesTab;
